import { useLocation } from "react-router-dom";
import { useSideNavMemory } from "@design-system/SideNav/useSideNavMemory";
import { isOnSubsectionPath } from "../isOnSubsectionPath";

/**
 * Expansion of a `SideNavItem` with sub-items, like today's nav subsections:
 * it expands on the parent's own page or a sub-item's page, collapses when
 * the route leaves them, and toggles freely in between. The current state
 * survives the nav hiding, showing and remounting. While the nav is unmounted
 * only the route on return is compared with the route it last saw, so a
 * round trip back to the same page made in between changes nothing. It also
 * tells the row when a sub-item is the page, for the mini nav.
 */
export const useRouteExpansion = ({
    parentPath,
    subItemPaths,
}: {
    parentPath: string;
    subItemPaths: string[];
}) => {
    const { pathname } = useLocation();
    const isSubItemActive = isOnSubsectionPath(pathname, subItemPaths);
    const isOnRoute = pathname === parentPath || isSubItemActive;
    const [isExpanded, setIsExpanded] = useSideNavMemory({
        id: `route:${parentPath}`,
        initialValue: isOnRoute,
    });
    const [wasOnRoute, setWasOnRoute] = useSideNavMemory({
        id: `route:${parentPath}:was-on-route`,
        initialValue: isOnRoute,
    });

    if (isOnRoute !== wasOnRoute) {
        setWasOnRoute(isOnRoute);
        setIsExpanded(isOnRoute);
    }

    return { isExpanded, onExpandedChange: setIsExpanded, isSubItemActive };
};
